import { G, S } from '@mobily/ts-belt';
import DownloadIcon from '@mui/icons-material/Download';
import {
  Box,
  Card,
  Dialog,
  DialogContent,
  ListItemButton,
  ListItemText,
  // No need for permissions check
  // eslint-disable-next-line @typescript-eslint/no-restricted-imports
  Button as MuiButton,
  Typography,
} from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import {
  FileField,
  RaRecord,
  TextField,
  useListContext,
  useRecordContext,
  useTranslate,
} from 'react-admin';

import { DialogTitleWithX } from '@sh4res/bo-common/components/DialogTitleWithX/DialogTitleWithX';
import { UTCDateField } from '@sh4res/bo-common/fields';
import { isBoExternal } from '@sh4res/bo-common/utils/env';

const defaultUseGetFileUrl =
  <T extends RaRecord>(urlSource?: Extract<keyof T, string>) =>
  (record: T | undefined) =>
    record && urlSource && record[urlSource] ? (record[urlSource] as string) : null;

export type DocumentListViewerLinkProps<T extends RaRecord> = {
  urlSource: Extract<keyof T, string>;
  nameSource: Extract<keyof T, string>;
  record?: T;
  label?: string;
  testId?: string;
  getFileUrl?: (
    urlSource?: Extract<keyof T, string>,
  ) => (entry: T, urlMap?: Map<string, string | null>) => Promise<string | null> | string | null;
};

export const DocumentListViewerLink = <T extends RaRecord>({
  urlSource,
  nameSource,
  record: recordOverride,
  getFileUrl = defaultUseGetFileUrl,
  testId,
}: DocumentListViewerLinkProps<T>) => {
  const list = useListContext<T>();
  const record = useRecordContext<T>({ record: recordOverride });

  const translate = useTranslate();
  const [state, setState] = useState<{
    file: T | null;
    url: string | null;
    isImage: boolean;
    isPreviewable: boolean;
  }>({
    file: null,
    url: null,
    isImage: false,
    isPreviewable: false,
  });
  const getUrl = getFileUrl(urlSource);

  const handleCloseModal = useCallback(
    () =>
      setState({
        file: null,
        url: null,
        isImage: false,
        isPreviewable: false,
      }),
    [],
  );

  const urlStore = useMemo(() => new Map<string, string | null>(), []);
  const onSelectedFile = useCallback(
    async (entry: T) => {
      const url = await getUrl(entry, urlStore);
      setState({
        file: entry,
        url,
        isImage: isImage(url),
        isPreviewable: isPreviewable(url),
      });
    },
    [urlStore, getUrl],
  );

  const onClick = useCallback(() => {
    if (!record) {
      return;
    }
    onSelectedFile(record);
  }, [onSelectedFile, record]);

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLSpanElement>) => {
      if (event.code.toLowerCase() === 'enter' || event.code.toLowerCase() === 'space') {
        event.preventDefault();
        onClick();
      }
    },
    [onClick],
  );

  const documents = useMemo(
    () =>
      G.isNotNullable(list.data) &&
      // only use list.data if the data contains the expected columns
      // otherwise it's from an outer context and does not reflect document data
      list.data.every((row) => urlSource in row && nameSource in row)
        ? list.data.filter((row) => G.isNotNullable(row[urlSource]) && S.isNotEmpty(row[urlSource]))
        : // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- we're checking record in next statement
          [record!],
    [list.data, record, urlSource, nameSource],
  );

  if (!record || list.isLoading) {
    return null;
  }

  return (
    <Box
      onClick={(event) => {
        event.stopPropagation();
      }}
      sx={{ width: 'fit-content' }}>
      {record[urlSource] !== null ? (
        <TextField
          data-testid={testId ? `${testId}_DocumentNameField` : undefined}
          source={nameSource}
          record={record}
          onClick={onClick}
          onKeyDown={onKeyDown}
          sx={{ color: '#594FF4', cursor: 'pointer', textDecoration: 'underline' }}
          tabIndex={0}
        />
      ) : null}

      <Dialog onClose={() => false} open={state.file !== null} fullWidth maxWidth="lg">
        <DialogTitleWithX onClose={handleCloseModal}>
          {translate('resources.bo_common.all_documents.list_modal.title')}
        </DialogTitleWithX>
        <DialogContent
          dividers
          sx={{
            display: 'flex',
            height: 'calc(100vh - 64px)',
          }}>
          <Card
            sx={{
              flex: 1,
              overflow: 'scroll',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              textAlign: 'center',
            }}>
            {state.url && state.file && state.isPreviewable && (
              <object
                key={state.file.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexDirection: 'column',
                  maxWidth: '100%',
                  maxHeight: '100%',
                  ...((() => !state.isImage)() && {
                    // For images it distorts the preview
                    height: '100%',
                    width: '100%',
                  }),
                }}
                data={state.url}>
                <Typography>
                  {translate('resources.bo_common.all_documents.list_modal.no_preview')}
                </Typography>
                <FileField<RaRecord>
                  data-testid={testId ? `${testId}_DocumentFileField` : undefined}
                  source={urlSource}
                  title={urlSource}
                  rel="noopener"
                  target="_blank"
                  record={state.file}
                />
              </object>
            )}
            {state.url && state.file && !state.isPreviewable && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  gap: 2,
                }}>
                <Typography>
                  {translate('resources.bo_common.all_documents.list_modal.no_preview')}
                </Typography>
                <FileField<RaRecord>
                  data-testid={testId ? `${testId}_DocumentFileField` : undefined}
                  source={urlSource}
                  title={urlSource}
                  rel="noopener"
                  target="_blank"
                  record={state.file}
                />
              </Box>
            )}
          </Card>
          <Box sx={{ width: '300px', marginLeft: 2, overflowY: 'scroll' }}>
            {documents.map((entry) => (
              <ListItemButton
                data-testid={testId ? `${testId}_DocumentListButton` : undefined}
                divider
                key={entry.id}
                onClick={async () => onSelectedFile(entry)}
                sx={{
                  backgroundColor: entry.id === state.file?.id ? '#F2F2F2' : 'white',
                }}>
                <ListItemText
                  primary={entry[nameSource]}
                  primaryTypographyProps={{
                    sx: {
                      color: '#594FF4',
                    },
                  }}
                  secondary={
                    <UTCDateField
                      data-testid={testId ? `${testId}_DocumentDateField` : undefined}
                      record={entry}
                      source="created_at"
                      showTime={!isBoExternal()}
                    />
                  }
                />
              </ListItemButton>
            ))}
            <Box
              sx={{
                position: 'absolute',
                bottom: '15px',
                right: '15px',
              }}>
              {state.url && (
                <MuiButton
                  data-testid={testId ? `${testId}_DownloadButton` : undefined}
                  variant="contained"
                  color="info"
                  disableElevation={true}
                  sx={{
                    height: '30px',
                    fontWeight: 'bold',
                  }}
                  href={state.url}
                  download
                  target="_blank"
                  startIcon={<DownloadIcon />}>
                  {translate('resources.bo_common.all_documents.list_modal.download')}
                </MuiButton>
              )}
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

const isImage = (gUrl: string | null) => {
  if (!gUrl) {
    return false;
  }
  try {
    const url = new URL(gUrl);
    const contentType = url.searchParams.get('response-content-type');
    return contentType?.startsWith('image') || !!url.pathname.match(/\.(jpg|jpeg|png|gif)$/i);
  } catch {
    return false;
  }
};

const isPreviewable = (gUrl: string | null) => {
  if (!gUrl) {
    return false;
  }
  try {
    const url = new URL(gUrl);
    return !!url.pathname.match(/\.(pdf|jpg|jpeg|png|gif|svg|webp|bmp|txt)$/i);
  } catch {
    return false;
  }
};
